import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import {
  BarChart3,
  Building2,
  CheckCircle2,
  Clock,
  DollarSign,
  FilePlus,
  FileText,
  FolderKanban,
  HardHat,
  Package,
  RefreshCw,
  Shield,
  Sparkles,
  Star,
  Ticket,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
  AlertTriangle,
  KeyRound,
} from 'lucide-react';
import { fetchAdminDashboardCore, fetchAdminDashboardSecondary } from '../api/adminDashboardApi';
import AdminClockWidget from '../components/AdminClockWidget';
import { QUICK_ACTIONS } from '../utils/adminDashboardNav';
import '../styles/AdminHomeDashboard.css';

const ICONS = {
  folder: FolderKanban,
  users: Users,
  hardhat: HardHat,
  dollar: DollarSign,
  file: FileText,
  shield: Shield,
  ticket: Ticket,
  building: Building2,
  filePlus: FilePlus,
  fileText: FileText,
  package: Package,
  sparkles: Sparkles,
  userPlus: UserPlus,
  barChart: BarChart3,
  check: CheckCircle2,
  clock: Clock,
};

function formatClockTime(d) {
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('complete')) return 'ahd-pill ahd-pill--green';
  if (s.includes('delay')) return 'ahd-pill ahd-pill--red';
  return 'ahd-pill ahd-pill--blue';
}

function stockClass(level) {
  if (level === 'Critical') return 'ahd-badge ahd-badge--critical';
  return 'ahd-badge ahd-badge--low';
}

function Empty({ message }) {
  return <p className="ahd-empty">{message}</p>;
}

export default function AdminHomeDashboard({ onQuickAccess }) {
  const displayName = useMemo(() => {
    const n = Cookies.get('userName') || Cookies.get('name');
    return n ? String(n).split(' ')[0] : 'Admin';
  }, []);

  const [now, setNow] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [dash, setDash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadingMore(false);
    setErrors([]);
    setWarnings([]);
    try {
      const core = await fetchAdminDashboardCore();
      const { _ctx, ...coreView } = core;
      setDash(coreView);
      setErrors(core.errors || []);
      setWarnings(core.warnings || []);
      setLoading(false);

      if (core.errors?.length) {
        return;
      }

      setLoadingMore(true);
      try {
        const secondary = await fetchAdminDashboardSecondary(_ctx);
        setDash((prev) => ({
          ...(prev || {}),
          ...secondary,
          // Keep core lead/project lists; refresh KPIs and widgets from secondary.
          leadOverview: prev?.leadOverview ?? coreView.leadOverview,
          activeProjects: prev?.activeProjects ?? coreView.activeProjects,
          errors: prev?.errors || [],
          warnings: [
            ...(core.warnings || []).filter(Boolean),
            ...(secondary.warnings || []).filter(Boolean),
          ],
        }));
        setWarnings((w) => {
          const next = [
            ...(core.warnings || []),
            ...(secondary.warnings || []),
          ].filter(Boolean);
          return next.length ? next : w;
        });
      } catch (secErr) {
        console.warn('[AdminHomeDashboard] secondary load failed:', secErr);
        setWarnings((w) => [
          ...w,
          'Some dashboard sections could not be loaded. Core KPIs are still shown.',
        ]);
      } finally {
        setLoadingMore(false);
      }
    } catch (e) {
      setErrors([e?.message || 'Failed to load dashboard']);
      setWarnings([]);
      setDash(null);
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const kpiMetrics = dash?.kpiMetrics ?? [];
  const pipelineStages = dash?.pipelineStages ?? [];
  const leadOverview = dash?.leadOverview ?? [];
  const activeProjects = dash?.activeProjects ?? [];
  const servicePerformance = dash?.servicePerformance ?? [];
  const workforceMetrics = dash?.workforceMetrics ?? [];
  const weeklyHours = dash?.weeklyHours ?? [0, 0, 0, 0, 0, 0, 0];
  const weeklyHourLabels = dash?.weeklyHourLabels ?? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const lowStock = dash?.lowStock ?? [];
  const equipmentStatus = dash?.equipmentStatus ?? [];
  const financeSummary = dash?.financeSummary;
  const upcomingMeetings = dash?.upcomingMeetings ?? [];
  const workforceSchedule = dash?.workforceSchedule ?? [];
  const slaMetrics = dash?.slaMetrics ?? [];
  const supportTickets = dash?.supportTickets ?? [];
  const activityTimeline = dash?.activityTimeline ?? [];
  const revenueTrend = dash?.revenueTrend ?? [];
  const productivityWeeks = dash?.productivityWeeks ?? [];

  const revenueHasData = revenueTrend.some((m) => (m.income ?? m.value) > 0);
  const revenueMax = Math.max(...revenueTrend.map((m) => m.value), 1);
  const prodMax = Math.max(...productivityWeeks.map((w) => w.value), 1);
  const hoursMax = Math.max(...weeklyHours, 1);
  const revenueDenom = Math.max(revenueTrend.length - 1, 1);

  return (
    <div className="ahd">
      {loading ? (
        <div className="ahd-banner ahd-banner--loading">Loading dashboard data…</div>
      ) : null}

      {!loading && loadingMore ? (
        <div className="ahd-banner ahd-banner--loading">
          Updating charts and secondary sections…
        </div>
      ) : null}

      {errors.length > 0 ? (
        <div className="ahd-banner ahd-banner--error">
          <span>{errors.join(' ')}</span>
          <button type="button" className="ahd-retry" onClick={() => void load()}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      ) : null}

      {!errors.length && warnings.length > 0 ? (
        <div className="ahd-banner ahd-banner--warn">
          <span>{warnings.join(' ')}</span>
          <button type="button" className="ahd-retry" onClick={() => void load()}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      ) : null}

      <section className="ahd-welcome">
        <div className="ahd-welcome__text">
          <h1>Welcome Back, {displayName}</h1>
          <p>Here&apos;s what&apos;s happening with your business today.</p>
        </div>
        <div className="ahd-welcome__actions">
          <div className="ahd-welcome__clock">{formatClockTime(now)}</div>
          <div className="ahd-welcome__status">
            <span className="ahd-dot ahd-dot--green" />
            Available
          </div>
        </div>
      </section>

      <div className="ahd-clock-wrap">
        <AdminClockWidget />
      </div>

      <section className="ahd-kpi-grid">
        {kpiMetrics.map((k) => {
          const Icon = ICONS[k.icon] || FolderKanban;
          return (
            <article key={k.id} className="ahd-card ahd-kpi">
              <div className="ahd-kpi__icon">
                <Icon size={22} />
              </div>
              <div className="ahd-kpi__body">
                <p className="ahd-kpi__label">{k.label}</p>
                <p className="ahd-kpi__value">{k.value}</p>
                <p
                  className={
                    k.tone === 'warn'
                      ? 'ahd-kpi__delta ahd-kpi__delta--warn'
                      : 'ahd-kpi__delta ahd-kpi__delta--up'
                  }
                >
                  {k.delta}
                </p>
              </div>
            </article>
          );
        })}
      </section>

      <section className="ahd-card ahd-section">
        <h2 className="ahd-section__title">Quick Access</h2>
        <div className="ahd-quick-grid">
          {QUICK_ACTIONS.map((a) => {
            const Icon = ICONS[a.icon] || FilePlus;
            return (
              <button
                key={a.id}
                type="button"
                className="ahd-quick"
                onClick={() => onQuickAccess?.(a.id)}
              >
                <span className="ahd-quick__icon">
                  <Icon size={22} />
                </span>
                <span>{a.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <div className="ahd-row ahd-row--2">
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Pipeline Summary</h2>
          {pipelineStages.length ? (
            <ul className="ahd-pipeline">
              {pipelineStages.map((p) => (
                <li key={p.stage}>
                  <div>
                    <strong>{p.stage}</strong>
                    <span>{p.deals} deals</span>
                  </div>
                  <em>{p.amount}</em>
                </li>
              ))}
            </ul>
          ) : (
            <Empty message="No pipeline stages yet. Create a pipeline to see deals here." />
          )}
        </section>
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Lead Overview</h2>
          {leadOverview.length ? (
            <div className="ahd-lead-grid">
              {leadOverview.map((l) => (
                <div key={l.label} className="ahd-lead-card">
                  <p>{l.label}</p>
                  <h3>{l.value}</h3>
                  <div className="ahd-progress">
                    <div
                      className="ahd-progress__fill"
                      style={{ width: `${l.pct}%`, backgroundColor: l.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty message="No leads found." />
          )}
        </section>
      </div>

      <div className="ahd-row ahd-row--2">
        <section className="ahd-card ahd-section ahd-section--table">
          <h2 className="ahd-section__title">Active Projects</h2>
          {activeProjects.length ? (
            <div className="ahd-table-wrap">
              <table className="ahd-table">
                <thead>
                  <tr>
                    <th>Project Name</th>
                    <th>Client</th>
                    <th>Manager</th>
                    <th>Status</th>
                    <th>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {activeProjects.map((p) => (
                    <tr key={`${p.name}-${p.deadline}`}>
                      <td>{p.name}</td>
                      <td>{p.client}</td>
                      <td>{p.manager}</td>
                      <td>
                        <span className={statusClass(p.status)}>{p.status}</span>
                      </td>
                      <td>{p.deadline}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty message="No active projects." />
          )}
        </section>
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Service Performance</h2>
          {servicePerformance.length ? (
            <ul className="ahd-service-list">
              {servicePerformance.map((s) => (
                <li key={s.name}>
                  <div>
                    <strong>{s.name}</strong>
                    <span>{s.projects} projects</span>
                  </div>
                  <div className="ahd-service-list__meta">
                    {s.rating !== '—' ? (
                      <span className="ahd-rating">
                        <Star size={14} fill="#f59e0b" stroke="#f59e0b" />
                        {s.rating}
                      </span>
                    ) : null}
                    <em>{s.revenue}</em>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <Empty message="No service data yet." />
          )}
        </section>
      </div>

      <section className="ahd-card ahd-section">
        <h2 className="ahd-section__title">Workforce &amp; Timesheet Overview</h2>
        <div className="ahd-workforce-grid">
          {workforceMetrics.map((m) => (
            <div key={m.label} className={`ahd-workforce-tile ahd-workforce-tile--${m.tone}`}>
              <p>{m.label}</p>
              <h3>{m.value}</h3>
            </div>
          ))}
          <div className="ahd-workforce-chart ahd-workforce-tile ahd-workforce-tile--chart">
            <p>Weekly Hours</p>
            <div className="ahd-mini-bars">
              {weeklyHours.map((v, i) => (
                <div key={weeklyHourLabels[i] || i} className="ahd-mini-bars__col">
                  <div
                    className="ahd-mini-bars__bar"
                    style={{ height: `${(v / hoursMax) * 100}%` }}
                    title={`${weeklyHourLabels[i]}: ${v}h`}
                  />
                  <span className="ahd-mini-bars__label">{weeklyHourLabels[i]?.slice(0, 1)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="ahd-row ahd-row--2">
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Low Stock Alert</h2>
          {lowStock.length ? (
            <ul className="ahd-stock-list">
              {lowStock.map((s) => (
                <li key={s.item}>
                  <div>
                    <strong>{s.item}</strong>
                    <span>{s.units}</span>
                  </div>
                  <span className={stockClass(s.level)}>{s.level}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty message="No low-stock items." />
          )}
        </section>
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Equipment Status</h2>
          <div className="ahd-equip-grid">
            {equipmentStatus.map((e) => (
              <div key={e.label} className={`ahd-equip ahd-equip--${e.tone}`}>
                {e.tone === 'green' ? (
                  <CheckCircle2 size={28} />
                ) : e.tone === 'amber' ? (
                  <KeyRound size={28} />
                ) : (
                  <AlertTriangle size={28} />
                )}
                <p>{e.label}</p>
                <h3>{e.value}</h3>
              </div>
            ))}
          </div>
        </section>
      </div>

      {financeSummary ? (
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Finance &amp; Billing Overview</h2>
          <div className="ahd-finance-top">
            <div className="ahd-finance-stat">
              <p>Total Revenue</p>
              <h3>{financeSummary.revenue.value}</h3>
              <span className={financeSummary.revenue.up ? 'ahd-finance-stat--up' : 'ahd-finance-stat--down'}>
                {financeSummary.revenue.up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}{' '}
                {financeSummary.revenue.delta}
              </span>
            </div>
            <div className="ahd-finance-stat">
              <p>Total Expenses</p>
              <h3>{financeSummary.expenses.value}</h3>
              <span className={financeSummary.expenses.up ? 'ahd-finance-stat--up' : 'ahd-finance-stat--down'}>
                {financeSummary.expenses.up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}{' '}
                {financeSummary.expenses.delta}
              </span>
            </div>
            <div className="ahd-finance-stat">
              <p>Pending Payroll</p>
              <h3>{financeSummary.payroll.value}</h3>
              <span className="ahd-muted">{financeSummary.payroll.note}</span>
            </div>
            <div className="ahd-finance-stat">
              <p>Due Payments</p>
              <h3>{financeSummary.due.value}</h3>
              <span className="ahd-muted">{financeSummary.due.note}</span>
            </div>
          </div>
          <div className="ahd-invoice-bars">
            {financeSummary.invoices.map((inv) => (
              <div key={inv.label} className="ahd-invoice-row">
                <div className="ahd-invoice-row__head">
                  <span>{inv.label}</span>
                  <span>{inv.count} invoices</span>
                </div>
                <div className="ahd-progress">
                  <div
                    className="ahd-progress__fill"
                    style={{ width: `${inv.pct}%`, backgroundColor: inv.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className="ahd-row ahd-row--2">
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Upcoming Meetings</h2>
          {upcomingMeetings.length ? (
            <ul className="ahd-meeting-list">
              {upcomingMeetings.map((m) => (
                <li key={m.title}>
                  <div>
                    <strong>{m.title}</strong>
                    <span>{m.time}</span>
                  </div>
                  <span className={`ahd-tag ahd-tag--${m.tone}`}>{m.tag}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty message="No upcoming meetings scheduled." />
          )}
        </section>
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Upcoming Workforce Schedule</h2>
          {workforceSchedule.length ? (
            <ul className="ahd-schedule-list">
              {workforceSchedule.map((w) => (
                <li key={w.name}>
                  <div>
                    <strong>{w.name}</strong>
                    <span>
                      {w.location} · {w.shift}
                    </span>
                  </div>
                  <span className="ahd-tag ahd-tag--blue">{w.region}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty message="No workforce members found." />
          )}
        </section>
      </div>

      <div className="ahd-row ahd-row--2">
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">SLA Compliance</h2>
          <div className="ahd-sla-grid">
            {slaMetrics.map((s) => (
              <div key={s.label} className={`ahd-sla-tile ahd-sla-tile--${s.tone}`}>
                <p>{s.label}</p>
                <h3>{s.value}</h3>
              </div>
            ))}
          </div>
        </section>
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Support Tickets</h2>
          <div className="ahd-support-grid">
            {supportTickets.map((t) => (
              <div key={t.label} className={`ahd-support-tile ahd-support-tile--${t.tone}`}>
                <p>{t.label}</p>
                <h3>{t.value}</h3>
                {t.note ? <span className="ahd-muted">{t.note}</span> : null}
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="ahd-card ahd-section">
        <h2 className="ahd-section__title">Recent Activity Timeline</h2>
        {activityTimeline.length ? (
          <ul className="ahd-timeline">
            {activityTimeline.map((a) => {
              const Icon = ICONS[a.icon] || Clock;
              return (
                <li key={`${a.title}-${a.ago}`}>
                  <span className="ahd-timeline__icon">
                    <Icon size={18} />
                  </span>
                  <div className="ahd-timeline__body">
                    <strong>{a.title}</strong>
                    <p>{a.desc}</p>
                  </div>
                  <time>{a.ago}</time>
                </li>
              );
            })}
          </ul>
        ) : (
          <Empty message="No recent activity." />
        )}
      </section>

      <div className="ahd-row ahd-row--2">
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Monthly Revenue Trend</h2>
          {revenueTrend.length && revenueHasData ? (
            <div className="ahd-line-chart">
              <svg viewBox="0 0 320 140" className="ahd-line-chart__svg" aria-hidden>
                <line x1="20" y1="120" x2="300" y2="120" stroke="#e2e8f0" strokeWidth="1" />
                <polyline
                  fill="none"
                  style={{ stroke: 'var(--brand-primary, #4f46e5)' }}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={revenueTrend
                    .map((m, i) => {
                      const x = 20 + (i / revenueDenom) * 280;
                      const y = 120 - (m.value / revenueMax) * 90;
                      return `${x},${y}`;
                    })
                    .join(' ')}
                />
                {revenueTrend.map((m, i) => {
                  const x = 20 + (i / revenueDenom) * 280;
                  const y = 120 - (m.value / revenueMax) * 90;
                  return <circle key={`${m.month}-${i}`} cx={x} cy={y} r="5" style={{ fill: 'var(--brand-primary, #4f46e5)' }} />;
                })}
              </svg>
              <div className="ahd-line-chart__labels">
                {revenueTrend.map((m, i) => (
                  <span key={`${m.month}-${i}`}>{m.month}</span>
                ))}
              </div>
            </div>
          ) : (
            <Empty message="No revenue recorded in the last 6 months." />
          )}
        </section>
        <section className="ahd-card ahd-section">
          <h2 className="ahd-section__title">Workforce Productivity</h2>
          {productivityWeeks.length ? (
            <div className="ahd-bar-chart">
              {productivityWeeks.map((w) => (
                <div key={w.week} className="ahd-bar-chart__col">
                  <div
                    className="ahd-bar-chart__bar"
                    style={{ height: `${(w.value / prodMax) * 100}%` }}
                  />
                  <span>{w.week}</span>
                </div>
              ))}
            </div>
          ) : (
            <Empty message="No payroll trend data yet." />
          )}
        </section>
      </div>

      {!loading && !errors.length ? (
        <p className="ahd-footnote ahd-footnote--live">Live data from your company GraphQL APIs.</p>
      ) : null}
    </div>
  );
}
